import { Button, InputRef, Input, Space, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import Table, { ColumnsType } from "antd/es/table";
import type { ColumnType } from "antd/es/table/interface";
import type { FilterDropdownProps } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import Highlighter from "react-highlight-words";
import { useNavigate } from "react-router-dom";

import { header } from "../utils/salesAdminHeader";
import { AnnouncementListDataType } from "./types";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";

//Permission usage
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { __currentUser__ } from "../../../graphql/policies";
import { ServiceTypes } from "../../../types/types";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface SalesAnnouncementListProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const AnnouncementList = ({
  setSelectedService,
  setSelectSubItem,
}: SalesAnnouncementListProps) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [tableHeight, setTableHeight] = useState(425);
  const [announcements, setAnnouncements] = useState<
    AnnouncementListDataType[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);

  const currentUser = useReactiveVar(__currentUser__);
  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const pageSize = 20;
  const navigate = useNavigate();

  const searchInput = useRef<InputRef>(null);
  const handleSearch = (
    selectedKeys: any,
    confirm: Function,
    dataIndex: string
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: Function) => {
    clearFilters();
    setSearchText("");
  };

  const fetchData = async () => {
    const data = await apiCall({
      method: "GET",
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/contents/admin/announcements",
      header: header,
    });
    if (data) {
      setAnnouncements(data);
    }
  };

  const getColumnSearchProps = (dataIndex: string): ColumnType<any> => {
    const searchProps: ColumnType<any> = {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(
                e.target.value ? [e.target.value] : ([] as string[])
              )
            }
            onPressEnter={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearch(selectedKeys as string[], confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => clearFilters && handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
            {/* <Button
              type="link"
              size="small"
              onClick={() => {
                confirm({ closeDropdown: false });
                setSearchText(selectedKeys[0] as string);
                setSearchedColumn(dataIndex);
              }}
            >
              Filter
            </Button> */}
            <Button type="link" size="small" onClick={close}>
              close
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase()),
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    };

    if (dataIndex !== "companyNumber") {
      searchProps.render = (text) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        );
    }

    return searchProps;
  };

  const uniqueCategories = Array.from(
    new Set(announcements.map((item) => item.category))
  );

  const columns: ColumnsType<AnnouncementListDataType> = [
    {
      title: "번호",
      render: (_, __, index) =>
        announcements.length - (currentPage - 1) * pageSize - index,
      width: 50,
    },
    {
      title: "수신대상",
      key: "viewer",
      dataIndex: "viewer",
      render: (v) => (v === "all" ? "All" : "BM (SGM)"),
      sorter: (a, b) => a.viewer.localeCompare(b.viewer),
      width: 50,
    },
    {
      title: "분류",
      dataIndex: "category",
      key: "category",
      width: 50,
      filters: uniqueCategories.map((cat) => ({
        text: cat,
        value: cat,
      })),
      onFilter: (v, record) => {
        return record.category === v;
      },
    },
    {
      title: "제목",
      dataIndex: "title",
      key: "title",
      width: 400,
      ...getColumnSearchProps("title"),
    },
    {
      title: "게시일",
      dataIndex: "date",
      key: "date",
      width: 50,
      render: (v) => dayjs(v).format("YYYY-MM-DD"),
      sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
    },
  ];

  useEffect(() => {
    fetchData();
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
    const updateHeight = () => {
      if (window.innerHeight > 900) {
        setTableHeight(525);
      } else {
        setTableHeight(425);
      }
    };

    updateHeight();
  }, []);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="announcementListWrapper">
      <ContentTitle title="공지사항" />
      <div className="content-container">
        <div className="flex justify-end">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            className="mr-5"
            onClick={() => {
              navigate("/sales188/announcement/create");
            }}
          >
            작성
          </Button>
        </div>
        <div className="p-4">
          <Table
            columns={columns}
            dataSource={announcements}
            scroll={{ x: "max-content", y: tableHeight }}
            pagination={{
              current: currentPage,
              pageSize,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  navigate(
                    `/sales188/announcement/${record.contentId}/${
                      record.viewer === "all" ? "all" : "bm"
                    }`
                  );
                },
                style: { cursor: "pointer" },
              };
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AnnouncementList;
